import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import SideBar from "../Components/Sidebar";
import "./SubscriptionPage.css";
import { getSubscriptionPlans, startSubscriptionTrial, getSubscriptionStatus } from "../Services/SubscriptionApi";
import Loader from "../Components/Loader";

const SubscriptionPage = ({ embedded = false }) => {
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [plans, setPlans] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const isActiveSub = subStatus?.hasSubscription &&
    ['active', 'trialing', 'cancelling', 'past_due'].includes(subStatus?.status);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await getSubscriptionPlans();
        let data;
        if (typeof response === 'string') {
          data = JSON.parse(response);
        } else if (Array.isArray(response)) {
          data = response;
        } else {
          data = response?.data || [];
        }
        const transformedPlans = data.map((plan) => {
          const displayNameParts = plan.display_name.split(" - ");
          const planName = displayNameParts[0];
          const planBilling = displayNameParts[1];
          const priceInDollars = (plan.amount / 100).toFixed(2);
          const period = plan.billing_interval === "month" ? "/mo" : "/yr";

          let badge = null;
          if (plan.billing_interval === "year") {
            const monthlyPlan = data.find(p =>
              p.display_name.split(" - ")[0] === planName &&
              p.billing_interval === "month"
            );
            if (monthlyPlan) {
              const monthlyPrice = monthlyPlan.amount / 100;
              const savings = (monthlyPrice * 2).toFixed(2);
              badge = `Save ${savings}`;
            }
          }

          return {
            id: plan.id,
            name: planName,
            billing: planBilling,
            price: Number.parseFloat(priceInDollars),
            period: period,
            trial_period_days: plan.trial_period_days,
            display_name: plan.display_name,
            badge: badge,
            features: [
              "Unlimited podcast recordings",
              "AI-powered transcription",
              "Custom branding & overlays",
              "Priority support",
            ],
          };
        });

        setPlans(transformedPlans);
        setSelectedPlan(transformedPlans[0]?.id || 1);
      } catch (error) {
        console.error("Failed to fetch subscription plans:", error);
      }
    };

    const fetchStatus = async () => {
      try {
        const status = await getSubscriptionStatus();
        setSubStatus(status);
        if (status?.hasUsedTrial) {
          setHasUsedTrial(true);
        }
      } catch (error) {
        console.error("Failed to fetch subscription status:", error);
      }
    };

    Promise.all([fetchPlans(), fetchStatus()]).finally(() => setPageLoading(false));
  }, []);

  const isActivePlan = (plan) => {
    if (!isActiveSub || !subStatus?.planName) return false;
    return subStatus.planName === plan.display_name;
  };

  if (pageLoading) {
    const loader = (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <Loader message="Loading subscriptions..." />
      </div>
    );
    if (embedded) return loader;
    return (
      <div className="subscription-wrapper">
        <SideBar />
        <div className="subscription-container">
          {loader}
        </div>
      </div>
    );
  }

  const trialDays = plans.length > 0 ? plans[0].trial_period_days : 7;

  const getSubtitle = () => {
    if (isActiveSub) return "You're currently subscribed. Manage your plan below.";
    if (hasUsedTrial) return "Pick a plan to continue enjoying premium features.";
    return `Start with a ${trialDays}-day free trial. Cancel anytime.`;
  };

  const content = (
    <div className="subscription-container" style={embedded ? { margin: 0, padding: 0 } : undefined}>
      <div className="subscription-header">
        <h1 className="subscription-title">Choose Your Plan</h1>
        <p className="subscription-subtitle">
          {getSubtitle()}
        </p>
      </div>

        {!hasUsedTrial && !isActiveSub && (
          <div className="subscription-trial-banner">
            <span className="subscription-trial-icon">🎉</span>
            <span className="subscription-trial-text">
              {plans.length > 0 ? `${plans[0].trial_period_days}-day free trial included with every plan` : "7-day free trial included with every plan"}
            </span>
          </div>
        )}

        <div className="subscription-plans-row">
          {plans.map((plan) => {
            const active = isActivePlan(plan);
            const isSelected = !isActiveSub && selectedPlan === plan.id;
            return (
              <button
                key={plan.id}
                type="button"
                className={`subscription-card ${active ? "subscription-card-active" : ""} ${isSelected ? "subscription-card-selected" : ""} ${isActiveSub && !active ? "subscription-card-disabled" : ""}`}
                onClick={() => !isActiveSub && setSelectedPlan(plan.id)}
                aria-pressed={active || isSelected}
                aria-label={`${plan.name} ${plan.billing} plan at ${plan.price} per ${plan.period}`}
                style={isActiveSub && !active ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
              >
                {plan.badge && <div className="subscription-badge">{plan.badge}</div>}
                <div className="subscription-plan-header">
                  <span className="subscription-plan-name">{plan.name}</span>
                  <span className="subscription-plan-billing">{plan.billing}</span>
                </div>
                <div className="subscription-price-row">
                  <span className="subscription-dollar">$</span>
                  <span className="subscription-price-value">{plan.price}</span>
                  <span className="subscription-price-period">{plan.period}</span>
                </div>
                <ul className="subscription-feature-list">
                  {plan.features.map((f, i) => (
                    <li key={i} className="subscription-feature-item">
                      <span className="subscription-checkmark">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <div className={`subscription-select-btn ${active ? "subscription-select-btn-active" : ""} ${isSelected ? "subscription-select-btn-active" : ""}`}>
                  {active && "Active Plan"}
                  {!active && isSelected && "Selected"}
                  {!active && !isSelected && "Select Plan"}
                </div>
              </button>
            );
          })}
        </div>

        {!isActiveSub && (
          <>
            <button
              type="button"
              className="subscription-cta-button"
              aria-label={`Start free trial with ${selectedPlan} plan`}
              disabled={checkoutLoading}
              onClick={async () => {
                try {
                  setCheckoutLoading(true);
                  const data = await startSubscriptionTrial(selectedPlan);
                  if (data?.url) {
                    window.location.href = data.url;
                  }
                } catch (error) {
                  console.error("Failed to start trial:", error);
                } finally {
                  setCheckoutLoading(false);
                }
              }}
            >
              {checkoutLoading && 'Redirecting...'}
              {!checkoutLoading && hasUsedTrial && 'Upgrade'}
              {!checkoutLoading && !hasUsedTrial && `Start ${trialDays}-Day Free Trial`}
            </button>
            <p className="subscription-disclaimer">
              {hasUsedTrial
                ? `You'll be charged $${plans.find(p => p.id === selectedPlan)?.price || "0"}${plans.find(p => p.id === selectedPlan)?.period || "/mo"} immediately. Cancel anytime.`
                : `After your free trial, you'll be charged $${plans.find(p => p.id === selectedPlan)?.price || "0"}${plans.find(p => p.id === selectedPlan)?.period || "/mo"}. Cancel anytime before the trial ends and you won't be charged.`}
            </p>
          </>
        )}
      </div>
  );

  if (embedded) return content;

  return (
    <div className="subscription-wrapper">
      <SideBar />
      {content}
    </div>
  );
};

SubscriptionPage.propTypes = {
  embedded: PropTypes.bool,
};

export default SubscriptionPage;
